import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button, ButtonWithLoader } from '@neo/index'
import { cn } from '@neo/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Input } from '@neo/primitives/input'
import { PopoverClose, PopoverContent } from '@neo/primitives/popover'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import type { AxiosError } from 'axios'
import { isEmpty } from 'lodash'
import { Lock, Plus, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  useExternalAppsV2Query,
  useExternalOrgAppsQuery,
} from 'v2/pages/settings/integrations/queries'
import { EmptyState } from 'v2/shared/components/empty-state'
import type { SecretVariable } from 'v2/shared/services/external-apps-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { buildSecretVariableGroups } from '../../components/secret-variable-groups'
import { useAppVariablesQuery, useSecretVariablesQuery } from '../../queries'
import { SecretVariableFormComponent } from '../../secrets/secret-variable-form'
import type { AiActionNodeData } from '../../types'
import { getAppDetails } from '../../utils'

interface SecretVariablesContentProps {
  form: GenericFormType<AiActionNodeData['config']>
  arrayIndex: number
  onClose: () => void
}

export const SecretVariablesContent = ({
  form,
  arrayIndex,
  onClose,
}: SecretVariablesContentProps) => {
  const { data: appVarsResponse, isLoading, error } = useAppVariablesQuery()
  const { data: externalAppsResponse } = useExternalAppsV2Query()
  const { data: connectedAppsResponse } = useExternalOrgAppsQuery(true)
  const { data: secretVarsResponse } = useSecretVariablesQuery()
  const [searchTerm, setSearchTerm] = useState('')
  const [isAddingSecret, setIsAddingSecret] = useState(false)
  const [isSecretCreationPending, setIsSecretCreationPending] = useState(false)
  const handleVariableClick = (variable: SecretVariable) => {
    const currentCode = form.getFieldValue(`actions[${arrayIndex}].code_body`) as string
    let newCode = currentCode

    // Use display_name for the variable name in code, fallback to name if display_name is not available
    const variableName = variable.display_name || variable.name
    // Always use the original name for the environment variable lookup
    const envVarName = variable.name

    // First, check if we need to add import os
    const hasImportOs = currentCode.includes('import os')
    const envVar = `${variableName} = os.environ.get("${envVarName}")`
    const hasEnvVar = currentCode.includes(envVar)

    if (!hasImportOs && !hasEnvVar) {
      // Add both import and variable
      newCode = `import os\n${envVar}\n\n${currentCode}`
    } else if (!hasImportOs) {
      // Add just the import at the top
      newCode = `import os\n${currentCode}`
    } else if (!hasEnvVar) {
      // Find the position after import os
      const lines = currentCode.split('\n')
      const importOsIndex = lines.findIndex((line) => line.includes('import os'))

      // Insert the env var after the imports
      lines.splice(importOsIndex + 1, 0, envVar)
      newCode = lines.join('\n')
    }

    form.setFieldValue(`actions[${arrayIndex}].code_body`, newCode)
    onClose()
  }

  const filteredVars = useMemo(
    () =>
      buildSecretVariableGroups({
        appVariables: appVarsResponse,
        connectedApps: connectedAppsResponse,
        customSecrets: secretVarsResponse,
        searchTerm,
      }),
    [appVarsResponse, connectedAppsResponse, secretVarsResponse, searchTerm],
  )

  const firstKey = Object.keys(filteredVars)[0] ?? ''
  const [accordionValues, setAccordionValues] = useState<string[]>(firstKey ? [firstKey] : [])

  return (
    <PopoverContent className="max-h-[450px] flex p-4 w-[350px]" side="left" sideOffset={320}>
      <div className="flex flex-col gap-3 w-full">
        {isAddingSecret ? (
          <div className="flex flex-col gap-3 w-full">
            <div className="relative flex items-center justify-between">
              <div className="text-h4-500">Add Secret Variable</div>
              <button
                type="button"
                onClick={() => setIsAddingSecret(false)}
                data-testid="button-cancel-add-secret"
                aria-label="Cancel add secret variable"
              >
                <X size={16} />
              </button>
            </div>
            <SecretVariableFormComponent
              onSuccess={() => {
                setIsAddingSecret(false)
                setAccordionValues(['Custom Secrets'])
              }}
              setIsPending={setIsSecretCreationPending}
            />
            <div className="flex justify-end">
              <ButtonWithLoader
                type="submit"
                form="secret-variable-form"
                tooltipContent="Add secret variable"
                loading={isSecretCreationPending}
              >
                Create Secret
              </ButtonWithLoader>
            </div>
          </div>
        ) : (
          <>
            <div className="relative flex items-center justify-between">
              <div className="text-h4-500">Insert Secret Variable</div>
              <PopoverClose>
                <X size={16} />
              </PopoverClose>
            </div>
            <div className="relative">
              <Input
                placeholder="Search variables..."
                startIcon={Search}
                value={searchTerm}
                autoFocus
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="input-search-variables"
              />
            </div>
            <ScrollArea className="-mr-4 pr-4">
              {isLoading && (
                <div className="text-p-400 text-muted-foreground">Loading variables...</div>
              )}
              {error && <EmptyState type="app_error" text={getErrorText(error as AxiosError)} />}
              {!isLoading && !error && isEmpty(filteredVars) && (
                <EmptyState
                  type="no_data_stack"
                  text={searchTerm ? 'No matching variables found' : 'No secret variables found'}
                />
              )}
              <Accordion
                type="multiple"
                className="space-y-2"
                defaultValue={accordionValues}
                onValueChange={setAccordionValues}
              >
                {Object.entries(filteredVars).map(
                  ([appKey, { variables, connectedApp }], index) => {
                    const isCustomSecrets = appKey === 'Custom Secrets'
                    const appDetails = isCustomSecrets
                      ? null
                      : getAppDetails(connectedApp?.provider || '', externalAppsResponse)

                    return (
                      <AccordionItem key={appKey} value={appKey} className="border-none">
                        <AccordionTrigger className="py-2 hover:no-underline flex items-center gap-2 group">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className="text-p-600 shrink-0">
                              {isCustomSecrets ? (
                                <Lock className="w-4 h-4" />
                              ) : (
                                <img
                                  src={appDetails?.logo_url || connectedApp?.logo_url}
                                  alt={appKey}
                                  className={cn(
                                    'w-4 h-4',
                                    (appDetails?.logo_url || connectedApp?.logo_url) && 'bg-bg3',
                                  )}
                                />
                              )}
                            </div>
                            <TruncatedTextWithTooltip
                              className="text-p-500 text-foreground truncate text-left"
                              tooltipContent={appKey}
                            >
                              {index + 1}. {appKey}
                            </TruncatedTextWithTooltip>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-1">
                          <div className="space-y-2">
                            {variables.map((variable) => (
                              <button
                                key={variable.name}
                                type="button"
                                onClick={() => handleVariableClick(variable)}
                                className="w-full text-left space-y-0.5 hover:bg-hv1 p-1 px-2 cursor-pointer rounded-md"
                                data-testid={`button-variable-${variable.name.toLowerCase()}`}
                              >
                                <div className="text-s-400">
                                  {variable.display_name || variable.name}
                                </div>
                                {variable.description && (
                                  <TruncatedTextWithTooltip
                                    className="text-s-400 text-muted-foreground truncate"
                                    tooltipContent={variable.description}
                                  >
                                    {variable.description}
                                  </TruncatedTextWithTooltip>
                                )}
                              </button>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )
                  },
                )}
              </Accordion>
            </ScrollArea>
            <div className="flex border-t border-t-st2 pt-2 px-4 -my-2 -mx-4">
              <Button
                variant="outline"
                size="iconSm"
                tooltipContent="Add secret variable"
                onClick={() => setIsAddingSecret(true)}
                data-testid="button-add-secret"
              >
                <Plus size={16} />
              </Button>
            </div>
          </>
        )}
      </div>
    </PopoverContent>
  )
}
